import React, { useState } from 'react';
import {sendEventToGA} from "./../../utils.js";
import {CdaWebModalButtonsRow} from './cdawebmodalbuttonsrow.js';
import {CdaWebModalMoreOptionsContainer} from './cdawebmodalmoreoptionscontainer.js';


/**
 * Modal table that display available parameters and hold other components such as plot button.
 * @param prop.variable Array of string that is used in query as parameters.
 * @param prop.url String that can be used to make requests to CDAWeb for given dataset.
 * @param props.startTime Datetime string that indicates the start time of interest.
 * @param props.stopTime Datetime string that indicates the stop time of interest.
 * @param props.checkedRows Array of number that keeps track of checked rows.
 * @param props.setCheckedRow Function that deals with changes to checkedRows.
 * @param prop.onGetDataFromCDAWeb Function that will make requests to CDAWeb server.
 * @param props.loading Boolean that indicates status of request of server.
 * @param props.displayLoading  Function that performs actions while data is loading
 * @param props.checkedOptions Array of number that keeps track of checked query parameters.
 * @param props.setCheckedOptions Function that deals with changes to checkedOptions.
 * @param props.sImageType String that indicated selected image format.
 * @param props.setSImageType Function that deals with changes made to selected image format (sImageType).
 * @return JSX
 */
export function CdaWebModalTable(props) {
    const { title, variables, url, startTime, stopTime, checkedRows, setCheckedRows, handleError} = props;
    const { onGetDataFromCDAWeb, loading, displayLoading, checkedOptions, setCheckedOptions, sImageType, setSImageType} = props;
    const [showOptions, setShowOptions] = useState(true);
    const [allOptions, setAllOptions] = useState([]);

    const handleCheckboxChange = (index, isChecked) => {
        if (isChecked) {
            setCheckedRows([...checkedRows, index]);
        } else {
            setCheckedRows(checkedRows.filter(row => row !== index));
        }
    };

    const handleOptionsChange = (option, isChecked) => {
        const idObj = {id: option.label, queryId: option.queryId };
        if (isChecked) {
            setCheckedOptions([...checkedOptions, idObj]);
        } else {
            setCheckedOptions(checkedOptions.filter((el) => {return el.id !== option.label;}));
        }
    };

    const handleRadioButtonChange = (event) => {
        setSImageType(event.target.value);
    };

    const addOptions = (newOptions) => {
        setAllOptions([...allOptions, ...newOptions]);
    }

    const toggleMoreOptions = () => {
      setShowOptions(!showOptions);
    };

    const handleCdaWeb = () => {
         window.open(url);
    }

    const handleSelectAll = () => {
        setCheckedRows(variables.map((el, i) => {return i}));
    }

    const handleUnselectAll = () => {
        setCheckedRows([]);
    }

    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    }


    return (
      <div className="cdaWebModalTableSection">
        <table className="cdaWebModalTable">
          <thead className="cdaWebModalTableHeader">
            <tr className="cdaWebModalTableHeader">
              <th className="cdaWebModalTableHeaderSelect">Select</th>
              <th className="cdaWebModalTableHeaderLongName">Variable</th>
              <th className="cdaWebModalTableHeaderParameter">Name</th>
            </tr>
          </thead>
          <tbody>
            {variables?.map((el, index) => (
              <tr key={index} onClick={(e) => handleCheckboxChange(index, !checkedRows.includes(index))}>
                <td className="cdaWebModalTableInput">
                  <input
                    type="checkbox"
                    checked={checkedRows.includes(index)}
                    onChange={(e) => handleCheckboxChange(index, e.target.checked)} />
                </td>
                <td className="cdaWebModalTableHeaderLongName">{capitalizeFirstLetter(el.LongDescription.replaceAll('--->','').trim())}</td>
                <td className="cdaWebModalTableHeaderParameter">{el.Name}</td>
              </tr>))}
          </tbody>
        </table>
        <br />
        <div className="cdaWebModalTableButtonRow">
          <div>
            <CdaWebModalTableButtonsRow
              title = {title}
              handleSelectAll = {handleSelectAll}
              handleUnselectAll = {handleUnselectAll}
              handleCdaWeb = {handleCdaWeb} />
          </div>
         <div>
            <CdaWebModalButtonsRow
              startTime = {startTime}
              stopTime = {stopTime}
              title = {title}
              onGetDataFromCDAWeb = {onGetDataFromCDAWeb}
              handleError = {handleError}
              loading={loading}
              displayLoading = {displayLoading}
              addOptions = {(o) => addOptions(o)} />
          </div>
        </div>
        <div className="cdaWebModalMoreOptionsButton" onClick={toggleMoreOptions}>More Options &#9660;</div>
        <div className={`cdaWebModalMoreOptionsContainer ${showOptions ? 'showOptions' : ''}`}>
          <CdaWebModalMoreOptionsContainer
            allOptions={allOptions}
            checkedOptions = {checkedOptions}
            handleOptionsChange = {handleOptionsChange}
            currentRadioBtn = {sImageType}
            setRadioButton = {handleRadioButtonChange} />
        </div>

      </div>);
 }


/**
 * Modal buttons that deal with interacting with plot result that is returned from CDAWeb.
 * @param prop.handleSelectAll Function that deals with selecting all available options.
 * @param props.handleUnselectAll Function that deals with unselecting all availabe options.
 * @param props.handleCdaWeb Function that open a new window/tab for current dataset. Note that this triggers new request to CDAWeb
 * @return JSX
 */
function CdaWebModalTableButtonsRow(props) {
    const {title, handleSelectAll, handleUnselectAll, handleCdaWeb} = props;

    const buttons = [
      { id: 1, text: 'Select All', action: handleSelectAll},
      { id: 2, text: 'Unselect All', action: handleUnselectAll},
      { id: 3, text: 'Open CDAWeb', action: handleCdaWeb},
    ];

    const handleClick = (button) => {
        if (button.action) {
            if(button.text === 'Open CDAWeb') {
                sendEventToGA(`dataset_access__cdaweb_open_cdaweb_${button?.text?.replace(" ", "_")}__button`, title);
            }
            button.action();
        }
    };

    return (
       <div className="cdaWebModalButtonRow">
         {buttons.map((button, index) => (
           <button key={index} className="cdaWebModalButton" onClick={()=>handleClick(button)} >{button.text}</button>
         ))}
       </div>
    );
}
